import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SESSION_STATUSES } from '../utils/constants';
import { formatDate } from '../utils/timeFormat';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import './AdminDashboardPage.css';

const STATUS_COLORS = {
  [SESSION_STATUSES.PENDING]:          '#f59e0b',
  [SESSION_STATUSES.ACCEPTED]:         '#60a5fa',
  [SESSION_STATUSES.PAID]:             '#4ade80',
  [SESSION_STATUSES.COMPLETED]:        '#a78bfa',
  [SESSION_STATUSES.DECLINED]:         '#f87171',
  [SESSION_STATUSES.CANCELLED]:        '#6b7280',
  [SESSION_STATUSES.CHANGE_SUGGESTED]: '#60a5fa',
};

function getLast6Months() {
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('default', { month: 'short' }),
    });
  }
  return months;
}

function groupByMonth(items, dateField) {
  const months = getLast6Months();
  const counts = {};
  months.forEach(m => { counts[m.key] = 0; });
  items.forEach(item => {
    if (!item[dateField]) return;
    const key = item[dateField].slice(0, 7);
    if (counts[key] !== undefined) counts[key]++;
  });
  return months.map(m => ({ label: m.label, count: counts[m.key] }));
}

function ConfirmModal({ modal, onConfirm, onCancel }) {
  const [reason, setReason] = React.useState('');
  if (!modal) return null;
  return (
    <div className="adm-modal-overlay" onClick={onCancel}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <h3 className="adm-modal-title">{modal.title}</h3>
        <p className="adm-modal-msg">{modal.message}</p>
        {modal.showReason && (
          <textarea
            className="adm-modal-textarea"
            placeholder="Reason (optional)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={2}
          />
        )}
        <div className="adm-modal-actions">
          <button className="adm-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="adm-btn-danger" onClick={() => onConfirm(reason)}>{modal.confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="adm-stat">
      <div className="adm-stat-label">{label}</div>
      <div className={`adm-stat-num${color ? ` adm-stat-${color}` : ''}`}>{value}</div>
    </div>
  );
}

function AdminDashboardPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [userFilter, setUserFilter] = useState('all');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [actionError, setActionError] = useState('');

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, sessionsRes, ratingsRes] = await Promise.all([
        supabase.from('profiles').select('*').order('created_at', { ascending: false }),
        supabase.from('session_requests').select(`
          *,
          parent:profiles!session_requests_parent_id_fkey(id, first_name, last_name),
          trainer:profiles!session_requests_mentor_id_fkey(id, first_name, last_name, avatar_url)
        `).order('created_at', { ascending: false }),
        supabase.from('ratings').select('*'),
      ]);
      if (usersRes.error) throw usersRes.error;
      if (sessionsRes.error) throw sessionsRes.error;
      if (ratingsRes.error) throw ratingsRes.error;
      setUsers(usersRes.data || []);
      setSessions(sessionsRes.data || []);
      setRatings(ratingsRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  const callAdminFunction = async (action, targetId) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch('/.netlify/functions/admin-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, targetId }),
    });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Admin action failed');
    }
    return res.json();
  };

  const handleDeleteUser = (userId, name) => {
    setConfirmModal({
      title: 'Delete User Account',
      message: `Are you sure you want to permanently delete ${name}? This cannot be undone and will remove all their data.`,
      confirmLabel: 'Delete Account',
      onConfirm: async () => {
        try {
          setActionError('');
          await callAdminFunction('deleteUser', userId);
          setConfirmModal(null);
          await fetchAllData();
        } catch (err) {
          setActionError(err.message);
          setConfirmModal(null);
        }
      },
    });
  };

  const handleDeletePhoto = (userId, name) => {
    setConfirmModal({
      title: 'Delete Profile Photo',
      message: `Remove ${name}'s profile photo? They can upload a new one at any time.`,
      confirmLabel: 'Delete Photo',
      onConfirm: async () => {
        try {
          setActionError('');
          await callAdminFunction('deletePhoto', userId);
          setConfirmModal(null);
          await fetchAllData();
        } catch (err) {
          setActionError(err.message);
          setConfirmModal(null);
        }
      },
    });
  };

  const handleCancelSession = (sessionId, sport, parentName) => {
    setConfirmModal({
      title: 'Cancel Session',
      message: `Cancel the ${sport} session for ${parentName}? Both parties will see it as cancelled.`,
      confirmLabel: 'Cancel Session',
      showReason: true,
      onConfirm: async (reason) => {
        try {
          setActionError('');
          const { error } = await supabase
            .from('session_requests')
            .update({ status: SESSION_STATUSES.CANCELLED, cancel_reason: reason ? `[Admin] ${reason}` : '[Admin] Cancelled by administrator' })
            .eq('id', sessionId);
          if (error) throw error;
          setConfirmModal(null);
          await fetchAllData();
        } catch (err) {
          setActionError(err.message);
          setConfirmModal(null);
        }
      },
    });
  };

  const handleRemoveReview = (ratingId) => {
    setConfirmModal({
      title: 'Remove Review',
      message: 'Remove this review permanently? This cannot be undone.',
      confirmLabel: 'Remove Review',
      onConfirm: async () => {
        try {
          setActionError('');
          const { error } = await supabase.from('ratings').delete().eq('id', ratingId);
          if (error) throw error;
          setConfirmModal(null);
          await fetchAllData();
        } catch (err) {
          setActionError(err.message);
          setConfirmModal(null);
        }
      },
    });
  };

  const trainers = users.filter(u => u.role === 'mentor');
  const parents = users.filter(u => u.role === 'parent');
  const revenue = sessions.filter(s => s.status === SESSION_STATUSES.PAID || s.status === SESSION_STATUSES.COMPLETED).length * 4;

  const sessionsByMonth = groupByMonth(sessions, 'created_at');
  const completedByMonth = getLast6Months().map(m => {
    const count = sessions.filter(s =>
      (s.status === SESSION_STATUSES.COMPLETED || s.status === SESSION_STATUSES.PAID) &&
      s.created_at?.slice(0, 7) === m.key
    ).length;
    return { label: m.label, count };
  });

  const sessionsChartData = getLast6Months().map((m, i) => ({
    month: m.label,
    Requested: sessionsByMonth[i].count,
    Completed: completedByMonth[i].count,
  }));

  const parentsByMonth = groupByMonth(parents, 'created_at');
  const trainersByMonth = groupByMonth(trainers, 'created_at');
  const usersChartData = getLast6Months().map((m, i) => ({
    month: m.label,
    Parents: parentsByMonth[i].count,
    Trainers: trainersByMonth[i].count,
  }));

  const statusCounts = Object.values(SESSION_STATUSES).map(status => ({
    name: status.replace('_', ' '),
    value: sessions.filter(s => s.status === status).length,
    color: STATUS_COLORS[status] || '#6b7280',
  })).filter(s => s.value > 0);

  const trainerLeaderboardRating = trainers.map(t => {
    const trainerRatings = ratings.filter(r => {
      const s = sessions.find(sess => sess.id === r.session_id);
      return s?.mentor_id === t.id;
    });
    const avg = trainerRatings.length > 0
      ? (trainerRatings.reduce((sum, r) => sum + r.rating, 0) / trainerRatings.length).toFixed(1)
      : null;
    return { ...t, avgRating: avg ? parseFloat(avg) : 0, ratingCount: trainerRatings.length };
  }).filter(t => t.ratingCount > 0).sort((a, b) => b.avgRating - a.avgRating).slice(0, 5);

  const trainerLeaderboardBookings = trainers.map(t => {
    const bookings = sessions.filter(s =>
      s.mentor_id === t.id &&
      (s.status === SESSION_STATUSES.PAID || s.status === SESSION_STATUSES.COMPLETED || s.status === SESSION_STATUSES.ACCEPTED)
    ).length;
    return { ...t, bookings };
  }).filter(t => t.bookings > 0).sort((a, b) => b.bookings - a.bookings).slice(0, 5);

  const filteredUsers = users.filter(u => {
    const matchRole = userFilter === 'all' || (userFilter === 'trainer' && u.role === 'mentor') || (userFilter === 'parent' && u.role === 'parent');
    const matchSearch = !searchTerm || `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    return matchRole && matchSearch && !u.is_admin;
  });

  const filteredSessions = sessions.filter(s => {
    return sessionFilter === 'all' || s.status === sessionFilter;
  });

  const recentUsers = [...users].filter(u => !u.is_admin).slice(0, 5);
  const recentSessions = [...sessions].slice(0, 5);

  const activeSessionStatuses = [SESSION_STATUSES.PENDING, SESSION_STATUSES.ACCEPTED, SESSION_STATUSES.PAID, SESSION_STATUSES.CHANGE_SUGGESTED];

  if (loading) {
    return (
      <div className="adm-wrapper">
        <div className="page-container adm-page">
          <div className="app-loading"><div className="app-loading-spinner" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="adm-wrapper">
      <div className="page-container adm-page">

        <div className="adm-header">
          <div>
            <h1 className="adm-title">Admin Dashboard</h1>
            <p className="adm-subtitle">GamePlannr platform overview</p>
          </div>
          <div className="adm-live-badge">Live</div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {actionError && <div className="alert alert-error">{actionError}</div>}

        <div className="adm-tabs">
          {['overview', 'users', 'sessions'].map(tab => (
            <button
              key={tab}
              className={`adm-tab${activeTab === tab ? ' adm-tab-active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            <div className="adm-stats">
              <StatCard label="Total Users" value={trainers.length + parents.length} color="blue" />
              <StatCard label="Trainers" value={trainers.length} color="green" />
              <StatCard label="Parents" value={parents.length} color="blue" />
              <StatCard label="Sessions" value={sessions.length} />
              <StatCard label="Revenue" value={`$${revenue}`} color="green" />
            </div>

            <div className="adm-charts-row">
              <div className="adm-chart-card">
                <div className="adm-chart-title">Sessions over time</div>
                <div className="adm-legend">
                  <span className="adm-legend-item"><span className="adm-legend-dot" style={{background:'#4ade80'}}></span>Completed</span>
                  <span className="adm-legend-item"><span className="adm-legend-dot" style={{background:'#60a5fa'}}></span>Requested</span>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={sessionsChartData} margin={{top:4,right:4,left:-20,bottom:0}}>
                    <XAxis dataKey="month" tick={{fill:'#8aaec8',fontSize:10}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fill:'#8aaec8',fontSize:10}} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{background:'#0f2035',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',fontSize:12}} cursor={{fill:'rgba(255,255,255,0.04)'}} />
                    <Bar dataKey="Requested" fill="#60a5fa" radius={[3,3,0,0]} />
                    <Bar dataKey="Completed" fill="#4ade80" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="adm-chart-card">
                <div className="adm-chart-title">Status breakdown</div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={statusCounts} cx="50%" cy="50%" innerRadius={45} outerRadius={68} paddingAngle={2} dataKey="value">
                      {statusCounts.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{background:'#0f2035',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',fontSize:12}} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="adm-legend" style={{flexWrap:'wrap'}}>
                  {statusCounts.map(s => (
                    <span key={s.name} className="adm-legend-item">
                      <span className="adm-legend-dot" style={{background:s.color}}></span>
                      {s.name} {s.value}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="adm-chart-card" style={{marginBottom:'12px'}}>
              <div className="adm-chart-title">New users over time</div>
              <div className="adm-legend">
                <span className="adm-legend-item"><span className="adm-legend-dot" style={{background:'#60a5fa'}}></span>Parents</span>
                <span className="adm-legend-item"><span className="adm-legend-dot" style={{background:'#4ade80'}}></span>Trainers</span>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={usersChartData} margin={{top:4,right:4,left:-20,bottom:0}}>
                  <XAxis dataKey="month" tick={{fill:'#8aaec8',fontSize:10}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fill:'#8aaec8',fontSize:10}} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{background:'#0f2035',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',fontSize:12}} cursor={{stroke:'rgba(255,255,255,0.1)'}} />
                  <Line type="monotone" dataKey="Parents" stroke="#60a5fa" strokeWidth={2} dot={{fill:'#60a5fa',r:3}} />
                  <Line type="monotone" dataKey="Trainers" stroke="#4ade80" strokeWidth={2} strokeDasharray="5 3" dot={{fill:'#4ade80',r:3}} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="adm-bottom-row">
              <div className="adm-section-card">
                <div className="adm-section-title">Top trainers by rating</div>
                {trainerLeaderboardRating.length === 0 ? <p className="adm-empty">No ratings yet</p> : trainerLeaderboardRating.map((t, i) => (
                  <div key={t.id} className="adm-lb-row">
                    <span className="adm-lb-rank">{i + 1}</span>
                    <div className="adm-lb-info">
                      <div className="adm-lb-name">{t.first_name} {t.last_name}</div>
                      <div className="adm-lb-sport">{t.sport}</div>
                    </div>
                    <div className="adm-lb-stats">
                      <div className="adm-stars">{'★'.repeat(Math.round(t.avgRating))}{'☆'.repeat(5 - Math.round(t.avgRating))} {t.avgRating}</div>
                      <div className="adm-lb-count">{t.ratingCount} reviews</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="adm-section-card">
                <div className="adm-section-title">Top trainers by bookings</div>
                {trainerLeaderboardBookings.length === 0 ? <p className="adm-empty">No bookings yet</p> : trainerLeaderboardBookings.map((t, i) => (
                  <div key={t.id} className="adm-lb-row">
                    <span className="adm-lb-rank">{i + 1}</span>
                    <div className="adm-lb-info">
                      <div className="adm-lb-name">{t.first_name} {t.last_name}</div>
                      <div className="adm-lb-sport">{t.sport}</div>
                    </div>
                    <div className="adm-lb-stats">
                      <div className="adm-lb-sessions" style={{color:'#4ade80',fontWeight:800,fontSize:'15px'}}>{t.bookings}</div>
                      <div className="adm-lb-count">sessions</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="adm-bottom-row" style={{marginTop:'12px'}}>
              <div className="adm-section-card">
                <div className="adm-section-title">Recent signups</div>
                {recentUsers.map(u => (
                  <div key={u.id} className="adm-user-row">
                    <div className={`adm-av ${u.role === 'mentor' ? 'adm-av-trainer' : 'adm-av-parent'}`}>
                      {u.first_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div style={{flex:1}}>
                      <div className="adm-user-name">{u.first_name} {u.last_name}</div>
                      <div className="adm-user-meta">{u.sport || (u.role === 'parent' ? 'Parent' : 'Trainer')}</div>
                    </div>
                    <span className={`adm-role-badge ${u.role === 'mentor' ? 'adm-role-trainer' : 'adm-role-parent'}`}>
                      {u.role === 'mentor' ? 'Trainer' : 'Parent'}
                    </span>
                    <div className="adm-user-actions">
                      {u.role === 'mentor' && u.avatar_url && (
                        <button className="adm-icon-btn adm-icon-danger" onClick={() => handleDeletePhoto(u.id, u.first_name)}>Photo</button>
                      )}
                      <button className="adm-icon-btn adm-icon-danger" onClick={() => handleDeleteUser(u.id, `${u.first_name} ${u.last_name}`)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="adm-section-card">
                <div className="adm-section-title">Recent sessions</div>
                {recentSessions.map(s => (
                  <div key={s.id} className="adm-sess-row">
                    <div className="adm-sess-sport">{s.sport}</div>
                    <div className="adm-sess-names">{s.parent?.first_name} → {s.trainer?.first_name}</div>
                    <span className="adm-sess-badge" style={{background:`${STATUS_COLORS[s.status]}18`,color:STATUS_COLORS[s.status]}}>
                      {s.status === SESSION_STATUSES.PAID ? 'Scheduled' : s.status?.replace('_',' ')}
                    </span>
                    {activeSessionStatuses.includes(s.status) && (
                      <button className="adm-sess-cancel" onClick={() => handleCancelSession(s.id, s.sport, s.parent?.first_name)}>Cancel</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <>
            <div className="adm-filter-row">
              <input
                className="adm-search"
                placeholder="Search users..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <div className="adm-filter-btns">
                {['all','parent','trainer'].map(f => (
                  <button key={f} className={`adm-filter-btn${userFilter === f ? ' adm-filter-btn-active' : ''}`} onClick={() => setUserFilter(f)}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="adm-section-card">
              {filteredUsers.length === 0 ? <p className="adm-empty">No users found</p> : filteredUsers.map(u => {
                const userSessions = sessions.filter(s => s.parent_id === u.id || s.mentor_id === u.id).length;
                return (
                  <div key={u.id} className="adm-user-row">
                    <div className={`adm-av ${u.role === 'mentor' ? 'adm-av-trainer' : 'adm-av-parent'}`}>
                      {u.first_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div style={{flex:1}}>
                      <div className="adm-user-name">{u.first_name} {u.last_name}</div>
                      <div className="adm-user-meta">{u.sport || '—'} · {userSessions} sessions · Joined {formatDate(u.created_at?.slice(0,10))}</div>
                    </div>
                    <span className={`adm-role-badge ${u.role === 'mentor' ? 'adm-role-trainer' : 'adm-role-parent'}`}>
                      {u.role === 'mentor' ? 'Trainer' : 'Parent'}
                    </span>
                    <div className="adm-user-actions">
                      {u.role === 'mentor' && u.avatar_url && (
                        <button className="adm-icon-btn adm-icon-danger" onClick={() => handleDeletePhoto(u.id, u.first_name)}>Del Photo</button>
                      )}
                      <button className="adm-icon-btn adm-icon-danger" onClick={() => handleDeleteUser(u.id, `${u.first_name} ${u.last_name}`)}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {activeTab === 'sessions' && (
          <>
            <div className="adm-filter-row">
              <div className="adm-filter-btns">
                <button className={`adm-filter-btn${sessionFilter === 'all' ? ' adm-filter-btn-active' : ''}`} onClick={() => setSessionFilter('all')}>All</button>
                {Object.values(SESSION_STATUSES).map(s => (
                  <button key={s} className={`adm-filter-btn${sessionFilter === s ? ' adm-filter-btn-active' : ''}`} onClick={() => setSessionFilter(s)}>
                    {s === SESSION_STATUSES.PAID ? 'Scheduled' : s.replace('_',' ')}
                  </button>
                ))}
              </div>
            </div>
            <div className="adm-section-card">
              {filteredSessions.length === 0 ? <p className="adm-empty">No sessions found</p> : filteredSessions.map(s => (
                <div key={s.id} className="adm-sess-row adm-sess-row-full">
                  <div className="adm-sess-sport">{s.sport}</div>
                  <div className="adm-sess-names" style={{flex:1}}>{s.parent?.first_name} {s.parent?.last_name} → {s.trainer?.first_name} {s.trainer?.last_name}</div>
                  <div className="adm-sess-date">{s.proposed_date}</div>
                  <div className="adm-sess-amount" style={{color:'#4ade80',fontWeight:700,fontSize:'12px',minWidth:'36px'}}>{s.duration && s.trainer ? `$${(Number(s.duration) * 30).toFixed(0)}` : ''}</div>
                  <span className="adm-sess-badge" style={{background:`${STATUS_COLORS[s.status]}18`,color:STATUS_COLORS[s.status]}}>
                    {s.status === SESSION_STATUSES.PAID ? 'Scheduled' : s.status?.replace('_',' ')}
                  </span>
                  {activeSessionStatuses.includes(s.status) && (
                    <button className="adm-sess-cancel" onClick={() => handleCancelSession(s.id, s.sport, s.parent?.first_name)}>Cancel</button>
                  )}
                  {(s.status === SESSION_STATUSES.COMPLETED || (s.status === SESSION_STATUSES.PAID)) && ratings.filter(r => r.session_id === s.id).map(r => (
                    <button key={r.id} className="adm-icon-btn adm-icon-danger" style={{marginLeft:'4px'}} onClick={() => handleRemoveReview(r.id)}>Del Review</button>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}

      </div>

      <ConfirmModal
        modal={confirmModal}
        onConfirm={(reason) => confirmModal?.onConfirm(reason)}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}

export default AdminDashboardPage;
